import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MdClose, MdNotifications } from 'react-icons/md'
import { useI18n } from '../../../../i18n/I18nProvider'
import {
  DueReminder,
  dismissReminder,
  loadDueReminders,
} from '../../api/reminder'
import styles from './ReminderHost.module.scss'

const POLL_MS = 60000
const AUTO_CLOSE_MS = 120000
const notifiedIds = new Set<string>()

const notifyBrowser = (reminder: DueReminder, title: string) => {
  if (typeof Notification === 'undefined' || notifiedIds.has(reminder.id)) {
    return
  }

  notifiedIds.add(reminder.id)

  if (Notification.permission === 'granted') {
    new Notification(title, { body: reminder.title })
  }
}

const reminderKind = (type: string, t: (key: string) => string) => {
  if (type === 'event') return t('Event')
  if (type === 'todo') return t('To-do')
  return type
}

const ReminderHost = () => {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [reminders, setReminders] = useState<DueReminder[]>([])
  const shownAt = useRef<Map<string, number>>(new Map())

  const refresh = useCallback(async () => {
    try {
      const due = await loadDueReminders()
      setReminders(due)

      if (
        due.length > 0 &&
        typeof Notification !== 'undefined' &&
        Notification.permission === 'default'
      ) {
        Notification.requestPermission()
      }

      due.forEach((reminder) => notifyBrowser(reminder, t('Reminder')))
    } catch {
      setReminders([])
    }
  }, [t])

  useEffect(() => {
    refresh()
    const timer = window.setInterval(refresh, POLL_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        refresh()
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [refresh])

  const onDismiss = useCallback(
    async (id: string) => {
      shownAt.current.delete(id)
      setReminders((current) => current.filter((item) => item.id !== id))
      try {
        await dismissReminder(id)
      } catch {
        refresh()
      }
    },
    [refresh],
  )

  useEffect(() => {
    const now = Date.now()
    const timers = reminders.map((reminder) => {
      if (!shownAt.current.has(reminder.id)) {
        shownAt.current.set(reminder.id, now)
      }
      const elapsed = now - (shownAt.current.get(reminder.id) || now)
      const remaining = Math.max(0, AUTO_CLOSE_MS - elapsed)
      return window.setTimeout(() => {
        onDismiss(reminder.id)
      }, remaining)
    })

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer))
    }
  }, [reminders, onDismiss])

  const onOpen = (reminder: DueReminder) => {
    const openAt = Date.now()
    if (reminder.targetType === 'event') {
      navigate(
        `/events?openType=event&openId=${reminder.targetId}&openAt=${openAt}`,
      )
      return
    }
    if (reminder.targetType === 'todo') {
      navigate(
        `/notes/to-dos?openType=todo&openId=${reminder.targetId}&openAt=${openAt}`,
      )
    }
  }

  if (reminders.length === 0) {
    return null
  }

  return (
    <div className={styles.stack}>
      {reminders.map((reminder) => (
        <div
          key={reminder.id}
          className={styles.card}
          role="button"
          tabIndex={0}
          onClick={() => onOpen(reminder)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              onOpen(reminder)
            }
          }}
        >
          <MdNotifications className={styles.icon} />
          <div className={styles.body}>
            <div className={styles.label}>
              {t('Reminder')}
              {reminder.targetType ? (
                <span className={styles.kind}>
                  {reminderKind(reminder.targetType, t)}
                </span>
              ) : null}
            </div>
            <div className={styles.title}>{reminder.title}</div>
          </div>
          <button
            type="button"
            className={styles.dismiss}
            onClick={(event) => {
              event.stopPropagation()
              onDismiss(reminder.id)
            }}
            aria-label={t('Dismiss')}
          >
            <MdClose />
          </button>
        </div>
      ))}
    </div>
  )
}

export default ReminderHost
